import { useEffect } from 'react';
import { CheckCircle2, AlertCircle, Info, AlertTriangle, X } from 'lucide-react';
import { clsx } from 'clsx';
import { useAppStore } from '@/stores/app';

const ICONS: Record<string, React.ElementType> = {
  success: CheckCircle2,
  error: AlertCircle,
  warning: AlertTriangle,
  info: Info,
};

const STYLES: Record<string, string> = {
  success: 'border-emerald-200 bg-emerald-50 text-emerald-700',
  error: 'border-red-200 bg-red-50 text-red-700',
  warning: 'border-amber-200 bg-amber-50 text-amber-700',
  info: 'border-blue-200 bg-blue-50 text-blue-700',
};

const AUTO_DISMISS_MS = 5_000;

export function Toaster() {
  const notifications = useAppStore((s) => s.notifications);
  const dismiss = useAppStore((s) => s.dismissNotification);

  useEffect(() => {
    if (notifications.length === 0) return;
    const timers = notifications.map((n) =>
      setTimeout(() => dismiss(n.id), AUTO_DISMISS_MS),
    );
    return () => timers.forEach((t) => clearTimeout(t));
  }, [notifications, dismiss]);

  if (notifications.length === 0) return null;

  return (
    <div className="fixed bottom-6 right-6 z-50 flex flex-col gap-2 items-end pointer-events-none">
      {notifications.slice(-5).map((n) => {
        const Icon = ICONS[n.type] ?? Info;
        return (
          <div
            key={n.id}
            role="status"
            className={clsx(
              'pointer-events-auto flex items-start gap-2.5 max-w-sm pl-3 pr-2 py-2.5 rounded-lg border shadow-lg animate-slide-in-right',
              STYLES[n.type] ?? STYLES.info,
            )}
          >
            <Icon size={15} className="flex-shrink-0 mt-0.5" />
            <p className="text-xs font-medium flex-1 min-w-0 break-words">
              {n.message}
            </p>
            <button
              type="button"
              onClick={() => dismiss(n.id)}
              className="p-0.5 rounded hover:bg-black/5 flex-shrink-0"
              aria-label="Dismiss"
            >
              <X size={12} />
            </button>
          </div>
        );
      })}
    </div>
  );
}
